"use client";

import { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { motion, useScroll, useTransform } from "framer-motion";
import { theme } from "@/lib/theme";
import Image from "next/image";

interface SubsectionTitleProps {
  title: string;
  image?: string;
  imagePosition?: "left" | "right";
}

export const SubsectionTitle = ({ title, image, imagePosition = "left" }: SubsectionTitleProps) => {
  const ref = useRef<HTMLDivElement>(null);

  // Track scroll position relative to this element
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "start start"], // From bottom of viewport to top of viewport
  });

  // Map scroll progress to opacity: fade in over transition zone
  const opacity = useTransform(scrollYProgress, [0, 1], [0, 1]);

  // Map scroll progress to scale: zoom in during transition
  const scale = useTransform(scrollYProgress, [0, 1], [0.85, 1]);

  return (
    <Box
      ref={ref}
      sx={{
        width: "100%",
        position: "relative",
        marginY: {
          xs: 4, // 32px mobile
          sm: 6, // 48px desktop
        },
      }}
    >
      <motion.div
        style={{
          width: "100%",
          position: "relative",
          zIndex: 1,
          scale,
          opacity,
          display: "grid",
          gridTemplateColumns:
            imagePosition === "left"
              ? "repeat(auto-fit, minmax(min(100%, 400px), 1fr))"
              : "repeat(auto-fit, minmax(min(100%, 400px), 1fr))",
          gap: "48px",
          alignItems: "center",
        }}
      >
        {imagePosition === "left" && image && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Image
              src={image}
              alt=""
              width={400}
              height={400}
              unoptimized={image.toLowerCase().endsWith(".gif")}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                maxWidth: "400px",
              }}
            />
          </Box>
        )}

        <Typography
          variant="h3"
          component="h3"
          sx={{
            color: theme.colors.black,
            textAlign: "center",
          }}
        >
          {title}
        </Typography>

        {imagePosition === "right" && image && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Image
              src={image}
              alt=""
              width={400}
              height={400}
              unoptimized={image.toLowerCase().endsWith(".gif")}
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                maxWidth: "400px",
              }}
            />
          </Box>
        )}
      </motion.div>
    </Box>
  );
};
